/**
 * Loads a classic script that sets one global, and resolves with that global.
 *
 * The widget's bundles are IIFEs, which cannot split themselves, so a part only
 * some pages need (the booking link view, ACA-106) ships as its own script next
 * to the widget's and comes in this way. A global already on the page is used
 * as it is; a script that fails to load is taken out again, so a retry adds a
 * fresh one.
 */
export function loadGlobalScript<T>(
  doc: Document,
  win: Window,
  src: string,
  globalName: string
): Promise<T> {
  const existing = Reflect.get(win, globalName) as T | undefined
  if (existing) return Promise.resolve(existing)

  return new Promise<T>((resolve, reject) => {
    const script = doc.createElement('script')
    script.src = src
    script.async = true
    script.addEventListener('load', () => {
      const loaded = Reflect.get(win, globalName) as T | undefined
      if (loaded) resolve(loaded)
      else reject(new Error(`${src} loaded without ${globalName}`))
    })
    script.addEventListener('error', () => {
      script.remove()
      reject(new Error(`${src} failed to load`))
    })
    doc.head.appendChild(script)
  })
}
